import { useState } from 'react'
import { Send, Github, Linkedin, Mail, CheckCircle, AlertCircle } from 'lucide-react'
import { Modal } from '@tinkern/ui'

type FormState = 'idle' | 'sending' | 'success' | 'error'

const CONTACT_LINKS = [
  {
    href: 'mailto:keerthanvenkata@gmail.com',
    label: 'keerthanvenkata@gmail.com',
    icon: Mail,
    external: false,
  },
  {
    href: 'https://github.com/keerthanvenkata',
    label: 'keerthanvenkata',
    icon: Github,
    external: true,
  },
  {
    href: 'https://www.linkedin.com/in/venkata-keerthan/',
    label: 'venkata-keerthan',
    icon: Linkedin,
    external: true,
  },
]

const INPUT_CLASS =
  'w-full bg-black/30 border border-violet/20 rounded-lg px-4 py-3 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-violet/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed'

export default function ContactModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [state, setState] = useState<FormState>('idle')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim() || !email.trim() || !message.trim()) return
    setState('sending')

    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), message: message.trim() }),
      })

      if (!res.ok) throw new Error(`HTTP ${res.status}`)

      setState('success')
      setName('')
      setEmail('')
      setMessage('')
    } catch {
      setState('error')
    }
  }

  const disabled = state === 'sending' || state === 'success'

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Get in touch"
      size="lg"
      containerClassName="glass rounded-xl shadow-2xl"
      headerClassName="border-b border-violet/20"
      titleClassName="text-xl font-heading font-bold text-white"
      closeButtonClassName="text-gray-400 hover:text-electric-pink p-1 rounded-lg hover:bg-white/10 transition-colors"
    >
      <div className="p-6 sm:p-8 grid md:grid-cols-2 gap-8">

        {/* Left — message form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="contact-name" className="block text-xs text-gray-400 mb-1.5">
                Name
              </label>
              <input
                id="contact-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={disabled}
                placeholder="Your name"
                autoComplete="name"
                className={INPUT_CLASS}
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="block text-xs text-gray-400 mb-1.5">
                Email
              </label>
              <input
                id="contact-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={disabled}
                placeholder="you@example.com"
                autoComplete="email"
                className={INPUT_CLASS}
              />
            </div>
          </div>

          <div>
            <label htmlFor="contact-message" className="block text-xs text-gray-400 mb-1.5">
              Message
            </label>
            <textarea
              id="contact-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              rows={5}
              disabled={disabled}
              placeholder="What are you building? What do you need?"
              className={`${INPUT_CLASS} resize-none`}
            />
          </div>

          {state === 'success' && (
            <div className="flex items-center gap-2 text-sm text-neon-green bg-neon-green/10 border border-neon-green/30 rounded-lg px-4 py-3">
              <CheckCircle size={15} className="shrink-0" />
              Message sent. I&apos;ll be in touch.
            </div>
          )}

          {state === 'error' && (
            <div className="flex items-center gap-2 text-sm text-red-400 bg-red-400/5 border border-red-400/20 rounded-lg px-4 py-3">
              <AlertCircle size={15} className="shrink-0" />
              Something went wrong. Try emailing directly.
            </div>
          )}

          <div className="flex items-center gap-4 mt-1">
            <button
              type="submit"
              disabled={disabled}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet to-magenta rounded-lg font-heading font-semibold text-sm text-white hover:shadow-[0_0_20px_rgba(127,0,255,0.5)] transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {state === 'sending' ? 'Sending…' : (
                <><Send size={13} /> Send message</>
              )}
            </button>
            <span className="text-xs text-gray-500">Replies within 24h.</span>
          </div>
        </form>

        {/* Right — direct links */}
        <div className="flex flex-col gap-3 md:pl-8 md:border-l md:border-violet/10">
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">
            Prefer direct?
          </p>
          {CONTACT_LINKS.map(({ href, label, icon: Icon, external }) => (
            <a
              key={href}
              href={href}
              target={external ? '_blank' : undefined}
              rel={external ? 'noopener noreferrer' : undefined}
              className="group flex items-center gap-3 text-sm text-gray-300 hover:text-white transition-colors"
            >
              <div className="w-9 h-9 rounded-lg border border-violet/20 flex items-center justify-center group-hover:border-electric-pink/50 transition-colors shrink-0">
                <Icon size={15} className="text-gray-400 group-hover:text-electric-pink transition-colors" />
              </div>
              <span className="truncate">{label}</span>
            </a>
          ))}
        </div>

      </div>
    </Modal>
  )
}
